/**
 * @fileoverview V2 Assist Sidebar — Shows import field status per step + mock editor for testing.
 *
 * Displays imported values with their resolution status for the current step.
 * Includes a collapsible mock JSON editor for testing the hydration flow.
 */
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  FlaskConical,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BrowserAgentViewer } from '@/components/browser-use-scraper';
import { BrowserAgentMiniPlayer } from '@/components/case-ingestion/ingestion-workflow/BrowserAgentMiniPlayer';
import { SourceUrlsBar } from '@/components/case-ingestion/assist/SourceUrlsBar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useGetCaseIngestionQuery } from '@/services/api/caseIngestionService';
import type {
  CaseIngestionImport,
  ImportedField,
  ImportPayload,
} from '@/services/types/case-ingestion-assist-types';

import { STEPS } from '../navigation/steps';
import { useIngestion } from '../state/IngestionProvider';

// ── Field display helpers ────────────────────────────────────────────────────

/** Maps step IDs to payload section keys for displaying fields */
const STEP_FIELDS: Record<string, { section: string; fields: { key: string; label: string }[] }> = {
  case_info: {
    section: 'case_info',
    fields: [
      { key: 'date_of_loss', label: 'Date of Loss' },
      { key: 'statute_of_limitations_date', label: 'Statute of Limitations' },
      { key: 'incident_location', label: 'Incident Location' },
      { key: 'incident_summary', label: 'Incident Summary' },
      { key: 'representing_attorney', label: 'Attorney' },
      { key: 'case_manager', label: 'Case Manager' },
      { key: 'case_id', label: 'Case ID' },
      { key: 'external_cms_url', label: 'CMS URL' },
    ],
  },
  client_info: {
    section: 'client_info',
    fields: [
      { key: 'email', label: 'Email' },
      { key: 'phone_number', label: 'Phone' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'ssn', label: 'SSN' },
      { key: 'ssn_last_four', label: 'Last 4 SSN' },
      { key: 'gender', label: 'Gender' },
      { key: 'preferred_language', label: 'Language' },
      { key: 'address', label: 'Address' },
    ],
  },
  liability: {
    section: 'liability',
    fields: [
      { key: 'police_report_number', label: 'Report Number' },
      { key: 'police_department', label: 'Department' },
      { key: 'police_report_date', label: 'Report Date' },
      { key: 'at_fault_party', label: 'At-Fault Party' },
    ],
  },
  treatment: {
    section: 'treatment',
    fields: [
      { key: 'providers', label: 'Medical Providers' },
    ],
  },
  coverage: {
    section: 'coverage',
    fields: [
      { key: 'first_party', label: 'First Party' },
      { key: 'third_party', label: 'Third Party' },
      { key: 'health_insurance', label: 'Health Insurance' },
    ],
  },
};

/** Safely access a nested field from the import payload by section + key. */
function getFieldValue(payload: ImportPayload, section: string, key: string): ImportedField<unknown> | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sectionData = (payload as any)[section];
  if (!sectionData || typeof sectionData !== 'object') return null;
  const field = sectionData[key];
  if (!field || typeof field !== 'object' || !('presence' in field)) return null;
  return field as ImportedField<unknown>;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return `${value.length} item${value.length !== 1 ? 's' : ''}`;
  if (typeof value === 'object') {
    // Address
    if ('line1' in value) {
      const addr = value as { line1?: string; city?: string; state?: string };
      return [addr.line1, addr.city, addr.state].filter(Boolean).join(', ');
    }
    return JSON.stringify(value);
  }
  return String(value);
}

// ── Field Row ────────────────────────────────────────────────────────────────

const FieldRow: React.FC<{ label: string; field: ImportedField<unknown> }> = ({ label, field }) => {
  const isPresent = field.presence === 'present' && field.value != null;
  const isMissing = field.presence === 'missing';
  const isNA = field.presence === 'not_applicable';

  // Expand arrays into nested rows
  if (isPresent && Array.isArray(field.value) && field.value.length > 0) {
    const items = field.value as Record<string, unknown>[];
    return (
      <div className="py-1 space-y-1">
        <div className="flex items-center gap-1.5">
          <Check className="h-3 w-3 text-green-500 shrink-0" />
          <span className="text-[11px] text-zinc-600">{label}</span>
          <span className="text-[10px] text-zinc-400">({items.length})</span>
        </div>
        <div className="ml-4.5 pl-3 border-l border-zinc-100 space-y-0.5">
          {items.map((item, idx) => {
            const name = (item.name ?? item.carrier_name ?? `Item ${idx + 1}`) as string;
            const subtitle = getItemSubtitle(item);
            return (
              <div key={idx} className="py-0.5">
                <p className="text-[11px] font-medium text-zinc-700 truncate">{name}</p>
                {subtitle && (
                  <p className="text-[10px] text-zinc-400 truncate">{subtitle}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-2 py-1">
      <div className="flex items-center gap-1.5 min-w-0">
        {isPresent && <Check className="h-3 w-3 text-green-500 shrink-0" />}
        {isMissing && <Clock className="h-3 w-3 text-zinc-300 shrink-0" />}
        {isNA && <X className="h-3 w-3 text-zinc-300 shrink-0" />}
        <span className="text-[11px] text-zinc-600 truncate">{label}</span>
      </div>
      {isPresent && (
        <span className="text-[11px] text-zinc-500 truncate max-w-[60%] text-right">
          {formatValue(field.value)}
        </span>
      )}
      {isMissing && (
        <span className="text-[10px] text-zinc-400 italic">not found</span>
      )}
    </div>
  );
};

/** Get a subtitle for nested array items (providers, coverages) */
function getItemSubtitle(item: Record<string, unknown>): string | null {
  // Provider: type + city
  if ('type' in item || 'city' in item) {
    const parts = [item.type, item.city].filter(Boolean);
    return parts.length > 0 ? parts.join(' \u00b7 ') as string : null;
  }
  // Coverage: policy_number + adjuster_name
  if ('policy_number' in item || 'adjuster_name' in item) {
    const parts = [
      item.policy_number && `Policy: ${item.policy_number}`,
      item.adjuster_name && `Adj: ${item.adjuster_name}`,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' \u00b7 ') as string : null;
  }
  return null;
}

// ── Step Fields Panel ────────────────────────────────────────────────────────

const StepFieldsPanel: React.FC<{ stepId: string; payload: ImportPayload }> = ({ stepId, payload }) => {
  const config = STEP_FIELDS[stepId];
  if (!config) return null;

  const fields = config.fields.reduce<
    Array<{ key: string; label: string; field: ImportedField<unknown> }>
  >((result, fieldConfig) => {
    const field = getFieldValue(payload, config.section, fieldConfig.key);
    if (!field) {
      return result;
    }

    result.push({ ...fieldConfig, field });
    return result;
  }, []);

  if (fields.length === 0) return null;

  const presentCount = fields.filter((f) => f.field?.presence === 'present').length;

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
          {STEPS.find((s) => s.id === stepId)?.label ?? stepId}
        </span>
        <span className="text-[10px] text-zinc-400">
          {presentCount}/{fields.length}
        </span>
      </div>
      {fields.map((field) => (
        <FieldRow key={field.key} label={field.label} field={field.field} />
      ))}
    </div>
  );
};

// ── Default mock payload ─────────────────────────────────────────────────────

const EMPTY_FIELD = { value: null, presence: 'missing' as const };

const DEFAULT_MOCK_PAYLOAD: ImportPayload = {
  case_info: {
    firm_name: EMPTY_FIELD,
    representing_attorney: { value: 'John Smith', presence: 'present' as const },
    case_manager: { value: 'Moyez Mansoor', presence: 'present' as const },
    date_of_loss: { value: '2024-06-15', presence: 'present' },
    statute_of_limitations_date: { value: '2026-06-15', presence: 'present' },
    incident_location: { value: '123 Main St, Los Angeles, CA 90012', presence: 'present' },
    incident_summary: { value: 'Client was rear-ended at a red light on Main St. Other driver was cited for following too closely.', presence: 'present' },
    case_type: { value: 'Motor Vehicle Accident', presence: 'present' },
    case_id: { value: 'FV-2024-8832', presence: 'present' },
    external_cms_url: { value: 'https://app.filevine.com/projects/123456', presence: 'present' },
  },
  client_info: {
    first_name: EMPTY_FIELD,
    last_name: EMPTY_FIELD,
    email: { value: 'jane.doe@email.com', presence: 'present' },
    phone_number: { value: '+13105551234', presence: 'present' },
    date_of_birth: { value: '1990-03-22', presence: 'present' },
    ssn: { value: '123-45-7890', presence: 'present' as const },
    ssn_last_four: { value: '7890', presence: 'present' as const },
    gender: { value: 'female', presence: 'present' as const },
    preferred_language: { value: 'Spanish', presence: 'present' as const },
    address: {
      value: { line1: '456 Oak Ave', line2: 'Apt 2B', city: 'Santa Monica', state: 'CA', postcode: '90401' },
      presence: 'present',
    },
  },
  companion_cases: {
    has_companions: { value: 'true', presence: 'present' as const },
    related_case_names: { value: ['Jessica Terry', 'Moyez Mansoor'], presence: 'present' as const },
  },
  liability: {
    police_report_number: { value: 'DR-2024-55789', presence: 'present' },
    police_report_date: { value: '2024-06-15', presence: 'present' },
    police_department: { value: 'LAPD - Central Division', presence: 'present' },
    at_fault_party: { value: 'John Smith', presence: 'present' },
    liability_notes: EMPTY_FIELD,
  },
  treatment: {
    providers: { value: [
      { name: 'Dr. Sarah Chen', type: 'Chiropractor', phone: '+13105559876', fax: null, address_line1: '789 Wilshire Blvd', address_line2: null, city: 'Los Angeles', state: 'CA', postcode: '90017', treatment_status: 'TREATING', care_level: 'CONSERVATIVE_CARE', facility_type: 'clinic' },
      { name: 'Kaiser Permanente - Orthopedics', type: 'Orthopedic Surgeon', phone: '+13105551111', fax: '+13105552222', address_line1: '4867 Sunset Blvd', address_line2: 'Suite 300', city: 'Los Angeles', state: 'CA', postcode: '90027', treatment_status: 'COMPLETED', care_level: 'SPECIALIST_CARE', facility_type: 'hospital' },
      { name: 'UCLA Medical Center ER', type: 'Emergency Room', phone: '+13108251234', fax: null, address_line1: '757 Westwood Plaza', address_line2: null, city: 'Los Angeles', state: 'CA', postcode: '90095', treatment_status: 'COMPLETED', care_level: 'EMERGENCY_CARE', facility_type: 'hospital' },
    ], presence: 'present' },
    treatment_notes: EMPTY_FIELD,
  },
  coverage: {
    first_party: { value: [{ carrier_name: 'State Farm', policy_number: 'SF-123456', claim_number: 'CL-789', named_insured: 'Jane Doe', policy_type: 'AUTO', adjuster_name: 'Mike Johnson', adjuster_phone: '+18005551111', adjuster_email: 'mjohnson@statefarm.com', adjuster_fax: null, policy_limit: '$25,000' }], presence: 'present' },
    third_party: { value: [{ carrier_name: 'Allstate', policy_number: 'AL-987654', claim_number: 'CL-456', named_insured: 'John Smith', policy_type: 'AUTO', adjuster_name: 'Sarah Williams', adjuster_phone: '+18005552222', adjuster_email: 'swilliams@allstate.com', adjuster_fax: null, policy_limit: '$50,000' }], presence: 'present' },
    health_insurance: { value: [{ carrier_name: 'Blue Cross Blue Shield', policy_number: 'BCBS-112233', claim_number: null, named_insured: 'Jane Doe', policy_type: 'HEALTH', adjuster_name: null, adjuster_phone: null, adjuster_email: null, adjuster_fax: null, policy_limit: null }], presence: 'present' },
    coverage_notes: EMPTY_FIELD,
  },
  source_notes: 'Imported from Filevine project #123456. Client referred by partner firm.',
};

// ── Main Component ───────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 2000;

function isActiveStatus(status: string | null | undefined): boolean {
  return status === 'pending' || status === 'in_progress';
}

function isTerminalStatus(status: string | null | undefined): boolean {
  return status === 'completed' || status === 'failed';
}

export const AssistSidebar: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { importData, setMockImport, state } = useIngestion();
  const currentStepId = STEPS[state.stepIndex]?.id ?? '';

  // Poll ingestion status for browser viewer
  const { data: ingestionResult } = useGetCaseIngestionQuery(state.matterId ?? '', {
    skip: !state.matterId,
    pollingInterval: POLL_INTERVAL_MS,
  });

  const ingestionStatus = ingestionResult?.ingestion_status ?? null;
  const browserUseWorkflowId =
    ingestionResult?.runtime_handle?.type === 'browser_use'
      ? ingestionResult.runtime_handle.workflow_id
      : null;

  const isActive = isActiveStatus(ingestionStatus);
  const showSpinner = isActive && !browserUseWorkflowId;
  const showBrowserViewer = isActive && !!browserUseWorkflowId;
  const showMiniPlayer = isTerminalStatus(ingestionStatus) && !!browserUseWorkflowId;
  const showError = ingestionStatus === 'failed';

  // Snap-to-URL: one-shot action, cleared after a short delay so the viewer
  // doesn't keep snapping back when the user manually scrubs.
  const [snapToUrl, setSnapToUrlRaw] = useState<string | null>(null);
  const snapTimeout = useRef<ReturnType<typeof setTimeout>>();
  const setSnapToUrl = useCallback((url: string) => {
    clearTimeout(snapTimeout.current);
    setSnapToUrlRaw(url);
    // Clear after the viewer has had time to process the snap
    snapTimeout.current = setTimeout(() => setSnapToUrlRaw(null), 500);
  }, []);

  // Extract source URLs for the current step
  const currentStepSourceUrls = useMemo(() => {
    if (!importData?.payload) return [];
    const config = STEP_FIELDS[currentStepId];
    if (!config) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sectionData = (importData.payload as any)[config.section];
    if (!sectionData || typeof sectionData !== 'object') return [];
    const urls = (sectionData as { source_urls?: string[] }).source_urls;
    return urls ?? [];
  }, [importData, currentStepId]);


  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(DEFAULT_MOCK_PAYLOAD, null, 2),
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [mockEditorOpen, setMockEditorOpen] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);

  // Show mock editor only while holding Ctrl+Shift (or Cmd+Shift on Mac)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) setShowDevTools(true);
    };
    const handleKeyUp = () => setShowDevTools(false);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleApply = useCallback(() => {
    try {
      const payload = JSON.parse(jsonText) as ImportPayload;
      const mock: CaseIngestionImport = {
        matter_id: state.matterId ?? '',
        imported_at: new Date().toISOString(),
        payload,
      };
      setMockImport(mock);
      setParseError(null);
    } catch (e) {
      setParseError((e as Error).message);
    }
  }, [jsonText, state.matterId, setMockImport]);

  const handleClear = useCallback(() => {
    setMockImport(null);
    setParseError(null);
  }, [setMockImport]);

  const hasActiveImport = !!importData;

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 rounded-2xl bg-white border border-zinc-100 shadow-md overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-blue-500" />
            <h2 className="text-xs font-semibold text-zinc-800">CMS Assist</h2>
            {hasActiveImport && (
              <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                Active
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* Phase 1: Spinner (pending, no workflow yet) */}
          {showSpinner && (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-xs text-zinc-500">
                {ingestionStatus === 'pending' ? 'Starting ingestion...' : 'Preparing browser agent...'}
              </p>
            </div>
          )}

          {/* Phase 2: Live browser viewer */}
          {showBrowserViewer && browserUseWorkflowId && (
            <div className="flex-1 overflow-hidden [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none">
              <BrowserAgentViewer workflowId={browserUseWorkflowId} compact hideHeader />
            </div>
          )}

          {/* Phase 3+: Terminal or no ingestion — show field status */}
          {!isActive && (
            <>
              {/* Error banner */}
              {showError && (
                <div className="border-b border-red-200 bg-red-50 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <div>
                      <p className="text-xs font-medium text-red-700">Ingestion failed</p>
                      {ingestionResult?.ingestion_error && (
                        <p className="mt-1 text-[11px] text-red-600 break-words">{ingestionResult.ingestion_error}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {hasActiveImport && importData?.payload ? (
                <div className="p-4 space-y-4">
                  {/* Current step fields only */}
                  {STEP_FIELDS[currentStepId] ? (
                    <StepFieldsPanel stepId={currentStepId} payload={importData.payload} />
                  ) : (
                    <p className="text-xs text-zinc-400 text-center py-4">
                      No imported fields for this step
                    </p>
                  )}

                  {/* Source URLs (below fields) */}
                  {currentStepSourceUrls.length > 0 && (
                    <SourceUrlsBar
                      urls={currentStepSourceUrls}
                      onSnapToUrl={browserUseWorkflowId ? setSnapToUrl : undefined}
                    />
                  )}

                  {/* Import metadata */}
                  {importData.imported_at && (
                    <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-100">
                      <Clock className="h-2.5 w-2.5 text-zinc-400" />
                      <span className="text-[10px] text-zinc-400">
                        Imported {new Date(importData.imported_at).toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 text-center">
                  <Sparkles className="h-6 w-6 text-zinc-300 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500">No import data available</p>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    Use the mock editor below to test
                  </p>
                </div>
              )}

              {/* Mini-player at bottom (when ingestion completed/failed) */}
              {showMiniPlayer && browserUseWorkflowId && (
                <div className="mt-auto">
                  <BrowserAgentMiniPlayer workflowId={browserUseWorkflowId} snapToUrl={snapToUrl} defaultHeight={400} />
                </div>
              )}
            </>
          )}
        </div>

        {/* Mock editor (collapsible, at bottom) — only visible while holding Ctrl+Shift */}
        {showDevTools && <div className="border-t border-zinc-100">
          <Collapsible open={mockEditorOpen} onOpenChange={setMockEditorOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-zinc-50 transition-colors">
              <div className="flex items-center gap-1.5">
                <FlaskConical className="h-3 w-3 text-zinc-400" />
                <span className="text-[11px] font-medium text-zinc-600">Mock Editor</span>
              </div>
              {mockEditorOpen ? (
                <ChevronDown className="h-3 w-3 text-zinc-400" />
              ) : (
                <ChevronRight className="h-3 w-3 text-zinc-400" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-4 space-y-2">
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  spellCheck={false}
                  className="w-full h-48 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-[10px] text-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                />
                {parseError && (
                  <p className="text-[10px] text-red-600">Invalid JSON: {parseError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleApply}
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700 transition-colors"
                  >
                    Apply Mock
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={!hasActiveImport}
                    className="rounded-lg border border-zinc-200 px-3 py-1.5 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>}
      </div>

    </div>
  );
};
